import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Mounts an IntersectionObserver that adds the `.in` class to every `.reveal`
 * element on the current page. Re-runs whenever the route changes so that
 * page transitions trigger the reveal animation again.
 */
export default function Reveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach((e) => e.classList.add("in"));
      return undefined;
    }
    els.forEach((e) => e.classList.remove("in"));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((e) => io.observe(e));
    // Ensure the top-of-page elements (already visible) reveal immediately
    requestAnimationFrame(() => {
      els.forEach((e) => {
        const rect = e.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.92) {
          e.classList.add("in");
          io.unobserve(e);
        }
      });
    });
    return () => io.disconnect();
  }, [pathname]);

  // also: scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}
